import { useEffect, useRef, useState } from 'react';
import { Send, Download, ArrowUpRight, Home, Clock, User, Wallet } from 'lucide-react';

const ICON_URL = 'https://customer-assets.emergentagent.com/job_pay-next-gen/artifacts/1oy2wqev_ICON.png';

const transactions = [
  { icon: '⬆', label: 'Payroll Deposit', sub: 'Direct Deposit', amount: '+$2,500.00', color: '#00FF00', time: 'Today, 9:00 AM' },
  { icon: '🏪', label: 'Main Street Café', sub: 'QR Pay — Merchant', amount: '-$12.50', color: '#A3A3A3', time: 'Today, 8:22 AM' },
  { icon: '⚡', label: 'FedNow Transfer', sub: 'Instant Send', amount: '-$350.00', color: '#A3A3A3', time: 'Yesterday' },
  { icon: '💰', label: 'Cash Back Reward', sub: 'Merchant Reward', amount: '+$4.15', color: '#00FF00', time: 'Yesterday' },
];

export default function ProductSection() {
  const sectionRef = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setRevealed(true); obs.disconnect(); } },
      { threshold: 0.1 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      id="product"
      data-testid="product-section"
      ref={sectionRef}
      className="relative section-padding"
      style={{
        scrollMarginTop: '80px',
        background: 'linear-gradient(180deg, #000 0%, #030803 50%, #000 100%)',
      }}
    >
      {/* Glow */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[700px] h-[500px]"
          style={{ background: 'radial-gradient(ellipse, rgba(0,255,0,0.06), transparent 70%)' }} />
      </div>

      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* LEFT: Phone mockup */}
          <div
            className="flex justify-center lg:justify-end"
            style={{
              opacity: revealed ? 1 : 0,
              transform: revealed ? 'translateX(0)' : 'translateX(-50px)',
              transition: 'all 0.9s cubic-bezier(0.16,1,0.3,1)',
            }}
          >
            <div className="relative" style={{ animation: 'float 5s ease-in-out infinite' }}>
              {/* Glow behind phone */}
              <div className="absolute inset-0 rounded-[44px]"
                style={{ background: 'radial-gradient(ellipse, rgba(0,255,0,0.2), transparent 70%)', filter: 'blur(30px)', transform: 'scale(0.9)' }} />

              {/* Phone frame */}
              <div
                className="relative overflow-hidden"
                style={{
                  width: '280px',
                  height: '580px',
                  borderRadius: '44px',
                  border: '2px solid rgba(0,255,0,0.35)',
                  background: '#000',
                  boxShadow: '0 0 60px rgba(0,255,0,0.12), 0 40px 80px rgba(0,0,0,0.8)',
                }}
              >
                {/* Notch */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 z-20"
                  style={{ width: '100px', height: '30px', background: '#000', borderRadius: '0 0 20px 20px' }} />

                {/* Status bar */}
                <div className="flex items-center justify-between px-6 pt-3 pb-1 text-[10px]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  <span className="text-white opacity-60">9:41</span>
                  <div className="w-24" />
                  <div className="flex items-center gap-1 text-white opacity-60">
                    <span>◼◼◼◼</span>
                    <span>100%</span>
                  </div>
                </div>

                {/* App content */}
                <div className="flex flex-col h-full pb-16 overflow-hidden">
                  {/* Header */}
                  <div className="flex items-center justify-between px-5 py-4">
                    <div>
                      <div className="text-[#00FF00] font-mono text-[9px] uppercase tracking-widest opacity-70">First Community Bank</div>
                      <div className="text-white font-black text-sm uppercase"
                        style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '11px', letterSpacing: '-0.5px' }}>
                        NexGenPay
                      </div>
                    </div>
                    <img src={ICON_URL} alt="NX" className="h-8 w-8 object-contain" />
                  </div>

                  {/* Balance card */}
                  <div className="mx-4 mb-4 p-4 relative overflow-hidden"
                    style={{
                      background: 'linear-gradient(135deg, #0A0A0A 0%, #0f1f0f 100%)',
                      border: '1px solid rgba(0,255,0,0.25)',
                      borderRadius: '16px',
                    }}
                  >
                    <div className="absolute top-0 right-0 w-24 h-24 opacity-20"
                      style={{ background: 'radial-gradient(circle, #00FF00, transparent)', filter: 'blur(20px)' }} />
                    <div className="text-[#00FF00] font-mono text-[10px] uppercase tracking-widest mb-1">Available Balance</div>
                    <div className="text-white font-black"
                      style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '26px', letterSpacing: '-1px' }}>
                      $2,450.00
                    </div>
                    <div className="flex items-center gap-1 mt-1">
                      <span className="text-[#00FF00] text-[10px]">▲ $2,500</span>
                      <span className="text-[#A3A3A3] text-[10px]">payroll today</span>
                    </div>
                  </div>

                  {/* Quick actions */}
                  <div className="grid grid-cols-4 gap-2 px-4 mb-4">
                    {[
                      { icon: Send, label: 'Send' },
                      { icon: Download, label: 'Receive' },
                      { icon: ArrowUpRight, label: 'Pay' },
                      { icon: Wallet, label: 'Cash Out' },
                    ].map(({ icon: Icon, label }) => (
                      <div key={label} className="flex flex-col items-center gap-1">
                        <div className="w-12 h-12 flex items-center justify-center"
                          style={{ background: 'rgba(0,255,0,0.08)', border: '1px solid rgba(0,255,0,0.2)', borderRadius: '12px' }}>
                          <Icon size={16} color="#00FF00" strokeWidth={1.5} />
                        </div>
                        <span className="text-[#A3A3A3] text-[9px] font-mono uppercase">{label}</span>
                      </div>
                    ))}
                  </div>

                  {/* Transactions */}
                  <div className="px-4 flex-1 overflow-hidden">
                    <div className="text-[#A3A3A3] font-mono text-[9px] uppercase tracking-widest mb-2">Recent Activity</div>
                    <div className="flex flex-col gap-0">
                      {transactions.map((t, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b border-[#1A1A1A] last:border-b-0">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 flex items-center justify-center text-sm"
                              style={{ background: '#0A0A0A', borderRadius: '8px', border: '1px solid #1A1A1A' }}>
                              {t.icon}
                            </div>
                            <div>
                              <div className="text-white text-[10px] font-semibold">{t.label}</div>
                              <div className="text-[#A3A3A3] text-[9px]">{t.sub}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-mono text-[11px] font-bold" style={{ color: t.color, fontFamily: 'JetBrains Mono, monospace' }}>
                              {t.amount}
                            </div>
                            <div className="text-[#A3A3A3] text-[8px]">{t.time}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Bottom nav */}
                <div className="absolute bottom-0 left-0 right-0 flex items-center justify-around py-3 px-4"
                  style={{ background: 'rgba(0,0,0,0.95)', borderTop: '1px solid #1A1A1A' }}>
                  {[
                    { Icon: Home, active: true },
                    { Icon: Wallet, active: false },
                    { Icon: Clock, active: false },
                    { Icon: User, active: false },
                  ].map(({ Icon, active }, i) => (
                    <div key={i} className="flex flex-col items-center">
                      <Icon size={18} color={active ? '#00FF00' : '#333'} strokeWidth={1.5} />
                    </div>
                  ))}
                </div>

                {/* Screen glow overlay */}
                <div className="absolute inset-0 pointer-events-none"
                  style={{ background: 'linear-gradient(to bottom, transparent 80%, rgba(0,0,0,0.5) 100%)' }} />
              </div>

              {/* Side label */}
              <div className="absolute -right-4 top-1/2 -translate-y-1/2 -rotate-90 origin-right">
                <span className="text-[#00FF00] font-mono text-[9px] uppercase tracking-widest opacity-50 whitespace-nowrap">
                  Your Brand · Your Bank · Your Revenue
                </span>
              </div>
            </div>
          </div>

          {/* RIGHT: Copy */}
          <div
            style={{
              opacity: revealed ? 1 : 0,
              transform: revealed ? 'translateX(0)' : 'translateX(50px)',
              transition: 'all 0.9s cubic-bezier(0.16,1,0.3,1) 0.15s',
            }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-[2px] bg-[#00FF00]" />
              <span className="text-[#00FF00] font-mono text-[11px] uppercase tracking-[0.4em]">Product in Action</span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-black uppercase text-white leading-none mb-4"
              style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.02em' }}>
              A Fully Branded
              <br /><span style={{ color: '#00FF00' }}>Mobile Wallet</span>
              <br />Your Bank Owns
            </h2>

            <p className="text-[#A3A3A3] text-base leading-relaxed mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
              What you see is exactly what your customers get — fully branded with your institution's name, your colors, and your domain. Zero NexGenPay branding. Your bank's wallet, powered by sovereign FedNow infrastructure.
            </p>

            <div className="flex flex-col gap-4 mb-8">
              {[
                { label: 'Instant FedNow Send & Receive', sub: 'Real-time settlement, sub-second transfers' },
                { label: 'SMS-Triggered Onboarding', sub: 'Reach unbanked customers with just a phone number' },
                { label: 'QR Pay Merchant Acceptance', sub: 'Sub-1% fees, zero chargebacks, bank-owned data' },
                { label: 'Cardless Cash Access', sub: 'ATM withdrawals without a physical card' },
              ].map((item, i) => (
                <div key={item.label} className="flex gap-3"
                  style={{
                    opacity: revealed ? 1 : 0,
                    transform: revealed ? 'translateX(0)' : 'translateX(20px)',
                    transition: `all 0.5s ease ${0.4 + i * 0.1}s`,
                  }}>
                  <div className="w-1 flex-shrink-0 mt-1" style={{ background: '#00FF00', height: '40px' }} />
                  <div>
                    <div className="text-white font-bold text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>{item.label}</div>
                    <div className="text-[#A3A3A3] text-xs" style={{ fontFamily: 'Manrope, sans-serif' }}>{item.sub}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => document.querySelector('#demo')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="product-demo-btn"
                className="btn-neon text-xs flex items-center gap-2"
                style={{ fontFamily: 'Unbounded, sans-serif', padding: '14px 24px' }}
              >
                Request a Demo
              </button>
              <button
                onClick={() => document.querySelector('#demo')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="product-download-btn"
                className="btn-outline-neon text-xs flex items-center gap-2"
                style={{ fontFamily: 'Unbounded, sans-serif', padding: '14px 24px' }}
              >
                <Download size={14} /> Download Feature List
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
